import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { playUrgentAlert, playNewRequestChime } from "@/lib/audio";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, CheckCircle2, XCircle, Inbox, Zap, AlertTriangle, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const urgencyStyles = {
  emergency: "urgency-emergency",
  high: "urgency-high",
  medium: "urgency-medium",
  low: "urgency-low",
};

function TimeSince({ date }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!date) return;
    const update = () => {
      const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
      if (s < 60) setText(`${s}s ago`);
      else if (s < 3600) setText(`${Math.floor(s / 60)}m ago`);
      else setText(`${Math.floor(s / 3600)}h ago`);
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [date]);
  return <span>{text}</span>;
}

export default function IncomingRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [urgentFlash, setUrgentFlash] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevRequestIds = useRef(new Set());

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await API.get("/requests/incoming");
      setRequests(data.requests);

      const hasUrgent = data.requests.some(
        (r) => r.urgency_level === "emergency" || r.urgency_level === "high"
      );

      // Detect new requests
      const currentIds = new Set(data.requests.map(r => r.id));
      const newRequests = data.requests.filter(r => !prevRequestIds.current.has(r.id));

      if (soundEnabled && prevRequestIds.current.size > 0 && newRequests.length > 0) {
        const hasNewUrgent = newRequests.some(
          r => r.urgency_level === "emergency" || r.urgency_level === "high"
        );
        if (hasNewUrgent) {
          playUrgentAlert();
        } else {
          playNewRequestChime();
        }
      }

      prevRequestIds.current = currentIds;

      if (hasUrgent) {
        setUrgentFlash(true);
        setTimeout(() => setUrgentFlash(false), 500);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [soundEnabled]);

  // Refresh every 10 seconds - urgent requests re-flash
  useEffect(() => {
    fetchRequests();
    const i = setInterval(fetchRequests, 10000);
    return () => clearInterval(i);
  }, [fetchRequests]);

  const handleAccept = async (requestId) => {
    setActionLoading(requestId);
    try {
      await API.post(`/requests/${requestId}/accept`);
      toast.success("Request accepted!");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePass = async (requestId) => {
    setActionLoading(requestId);
    try {
      await API.post(`/requests/${requestId}/pass`);
      toast.success("Passed on request");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pass");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const urgentCount = requests.filter(
    (r) => r.urgency_level === "emergency" || r.urgency_level === "high"
  ).length;

  return (
    <div className="space-y-4" data-testid="incoming-requests-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-zinc-900">Incoming Requests</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1.5 rounded-lg transition-colors ${soundEnabled ? "bg-zinc-100 text-zinc-700" : "bg-zinc-100 text-zinc-400"}`}
            title={soundEnabled ? "Sound alerts on" : "Sound alerts off"}
            data-testid="sound-toggle-button"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          {urgentCount > 0 && (
            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs urgent-flash" data-testid="urgent-count-badge">
              <Zap className="w-3 h-3 mr-1" /> {urgentCount} Urgent
            </Badge>
          )}
          <Badge variant="secondary" className="text-xs">
            {requests.length} pending
          </Badge>
        </div>
      </div>

      {requests.length === 0 ? (
        <Card className="border border-zinc-200">
          <CardContent className="p-8 text-center">
            <Inbox className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No incoming requests right now</p>
            <p className="text-xs text-zinc-400 mt-1">
              New requests will appear here - checking every 10 seconds
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req, idx) => {
            const isUrgent =
              req.urgency_level === "emergency" || req.urgency_level === "high";
            return (
              <Card
                key={req.id}
                className={`border overflow-hidden animate-in ${
                  idx < 4 ? `animate-delay-${idx + 1}` : ""
                } ${isUrgent ? "urgent-pulse border-red-400" : "border-zinc-200"}`}
                data-testid={`request-card-${req.id}`}
              >
                <CardContent className="p-4">
                  {/* Urgent Banner */}
                  {isUrgent && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg mb-3 border border-red-200" data-testid={`urgent-banner-${req.id}`}>
                      <AlertTriangle className="w-4 h-4 text-red-600 urgent-flash" />
                      <span className="text-xs font-bold text-red-700 uppercase tracking-wider urgent-flash">
                        {req.urgency_level === "emergency"
                          ? "EMERGENCY - Immediate Response Required"
                          : "HIGH PRIORITY - Quick Response Needed"}
                      </span>
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-400">
                          {req.request_code}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${urgencyStyles[req.urgency_level]}`}
                        >
                          {req.urgency_level?.toUpperCase()}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-zinc-900 mt-1">
                        {req.service_type_label}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isUrgent && (
                        <div className="status-dot emergency" />
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-zinc-600 mb-3 line-clamp-2">
                    {req.problem_description}
                  </p>

                  {/* Details */}
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {req.property_address_line1}, {req.property_city},{" "}
                        {req.property_state} {req.property_zip_code}
                      </span>
                    </div>
                    {req.customer_name && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span className="w-3 h-3 flex-shrink-0 text-center font-bold">
                          C
                        </span>
                        <span>{req.customer_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Time since posted */}
                  <div className="flex items-center gap-2 text-xs mb-4 p-2 bg-zinc-50 rounded-lg">
                    <Clock className={`w-3.5 h-3.5 ${isUrgent ? "text-red-500" : "text-zinc-400"}`} />
                    <span className={isUrgent ? "text-red-600 font-semibold" : "text-zinc-500"}>
                      Posted <TimeSince date={req.created_at} />
                    </span>
                    {isUrgent && (
                      <span className="ml-auto text-[10px] text-red-500 font-bold urgent-flash">
                        REFRESHING
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAccept(req.id)}
                      disabled={actionLoading === req.id}
                      className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white text-sm"
                      data-testid={`accept-request-${req.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handlePass(req.id)}
                      disabled={actionLoading === req.id}
                      className="flex-1 h-10 text-sm border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      data-testid={`pass-request-${req.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1.5" /> Pass
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
